import React from 'react';
import { ChevronLeft, MessageSquarePlus, Minus, X } from 'lucide-react';
import { cn } from '../../utils';
import { useT } from '../../i18n';
import { PositionMenu } from '../PositionMenu/PositionMenu';
import styles from './ChatHeader.module.css';

export interface ChatHeaderProps {
  title: string;
  backgroundColor?: string;
  textColor?: string;
  borderColor?: string;
  showBackButton?: boolean;
  showNewChatButton?: boolean;
  showPositionMenu?: boolean;
  onClose?: (e?: React.MouseEvent<HTMLElement>) => void;
  onPositionLeftHalf?: () => void;
  onPositionRightHalf?: () => void;
  onCollapseToDefault?: () => void;
  onBack?: () => void;
  onNewChat?: (e?: React.MouseEvent<HTMLElement>) => void;
  rightActions?: React.ReactNode;
  className?: string;
}

export function ChatHeader({
  title,
  backgroundColor = '#ffffff',
  textColor = '#1e293b',
  borderColor = '#e2e8f0',
  showBackButton = false,
  showNewChatButton = false,
  showPositionMenu = false,
  onClose,
  onBack,
  onNewChat,
  onPositionLeftHalf,
  onPositionRightHalf,
  onCollapseToDefault,
  rightActions,
  className,
}: ChatHeaderProps) {
  const t = useT();

  return (
    <div
      className={cn(styles.header, className)}
      style={{ backgroundColor, borderColor }}
    >
      <div className={styles.leftActions}>
        {showBackButton ? (
          <button
            onClick={onBack}
            aria-label={t('header.back')}
            className={styles.actionButton}
            style={{ color: textColor }}
            type="button"
          >
            <ChevronLeft size={20} strokeWidth={2} />
          </button>
        ) : (
          <>
            {onClose && (
              <button
                onClick={onClose}
                aria-label={t('header.minimize')}
                className={styles.actionButton}
                style={{ color: textColor }}
                type="button"
              >
                <Minus size={20} strokeWidth={2.5} />
              </button>
            )}
            {showNewChatButton && onNewChat && (
              <button
                onClick={onNewChat}
                aria-label={t('header.newChat')}
                className={styles.actionButton}
                style={{ color: textColor }}
                type="button"
              >
                <MessageSquarePlus size={20} strokeWidth={2} />
              </button>
            )}
          </>
        )}
      </div>

      <h3 className={styles.title} style={{ color: textColor }}>
        {title}
      </h3>

      <div className={styles.rightActions}>
        {rightActions}
        {showPositionMenu && onPositionLeftHalf && onPositionRightHalf && onCollapseToDefault && (
          <PositionMenu
            onPositionLeftHalf={onPositionLeftHalf}
            onPositionRightHalf={onPositionRightHalf}
            onCollapseToDefault={onCollapseToDefault}
            textColor={textColor}
          />
        )}
        {onClose && !showBackButton && (
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label={t('header.close')}
            style={{ color: `${textColor}80` }}
            type="button"
          >
            <X size={20} />
          </button>
        )}
      </div>
    </div>
  );
}
